import { useLayoutEffect, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  projects,
  findProjectBySlug,
  buildCaseStudyFrames
} from "../data/projects";
import { useTransition, FRAME_SCALE } from "../contexts/TransitionContext";
import { fetchCaseStudyOverrides, fetchCustomProjects } from "../lib/settingsApi";

// Hero frame (frame 1) — image at a centered "framed" size on a white background.
// Uses the EXACT same DOM/CSS structure as the Portfolio click-transition overlay's
// end state (absolute inset-0 + transform: scale(FRAME_SCALE) + origin-center) so the
// route swap is pixel-perfect with no visible jump.

function HeroFrame({ src }) {
  return (
    <section
      id="case-frame-1"
      data-testid="case-frame-1"
      className="portfolio-snap relative w-full h-screen overflow-hidden bg-paper"
    >
      {/* Visual-viewport tracker: matches the click-transition overlay so that
          when iOS Safari expands its chrome at handoff, BOTH the overlay and
          this HeroFrame translate by the same `--vv-y` (set by
          useVisualViewportCenter). The image stays at the visible center
          throughout the chrome animation → no visible jump when the overlay
          fades out. */}
      <div
        className="absolute inset-0"
        style={{ transform: "translateY(var(--vv-y, 0px))" }}
      >
        <motion.div
          data-testid="case-frame-1-image"
          initial={{ scale: FRAME_SCALE }}
          animate={{ scale: FRAME_SCALE }}
          transition={{ duration: 0 }}
          className="absolute inset-0 origin-center overflow-hidden will-change-transform"
        >
          <div className="absolute inset-[-22%]">
            <img
              src={src}
              alt=""
              loading="eager"
              className="w-full h-full object-cover pointer-events-none"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// Editorial frame (frames 2–5) — white background, two-column: copy + square placeholder or real image.
function EditorialFrame({ frameNumber, totalFrames, side, heading, body, hideImage, imageSrc }) {
  const isImageLeft = side === "left";

  return (
    <section
      id={`case-frame-${frameNumber}`}
      data-testid={`case-frame-${frameNumber}`}
      className="portfolio-snap relative w-full h-screen overflow-hidden bg-paper text-ink"
    >
      <div
        className={`relative z-10 w-full h-full max-w-[1400px] mx-auto px-8 md:px-16 py-24 md:py-28 flex flex-col gap-10 md:gap-16 ${
          hideImage
            ? "items-center justify-center text-center"
            : "md:flex-row items-center justify-between"
        }`}
      >
        {/* Image column — real image when imageSrc provided, otherwise placeholder. Hidden when hideImage is true. */}
        {!hideImage && (
          <div className={`order-2 ${isImageLeft ? "md:order-1" : "md:order-2"}`}>
          <div
            data-testid={`case-frame-${frameNumber}-image-placeholder`}
            className="aspect-square w-[58vmin] max-w-[560px] bg-[#ECEAE3] border border-ink/10 relative overflow-hidden"
          >
            {imageSrc ? (
              <img
                src={imageSrc}
                alt=""
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <>
                {/* Diagonal lines to make it read clearly as a placeholder */}
                <svg
                  className="absolute inset-0 w-full h-full text-ink/15"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  aria-hidden
                >
                  <line x1="0" y1="0" x2="100" y2="100" stroke="currentColor" strokeWidth="0.4" vectorEffect="non-scaling-stroke" />
                  <line x1="100" y1="0" x2="0" y2="100" stroke="currentColor" strokeWidth="0.4" vectorEffect="non-scaling-stroke" />
                  <rect x="0" y="0" width="100" height="100" fill="none" stroke="currentColor" strokeWidth="0.4" vectorEffect="non-scaling-stroke" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-ink/40">
                    Image · square
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
        )}

        {/* Copy column */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.4 }}
          transition={{ duration: 0.8, ease: [0.22, 0.61, 0.36, 1] }}
          className={`order-1 ${isImageLeft ? "md:order-2" : "md:order-1"} max-w-[520px]`}
        >
          {heading && (
            <h2 className="font-display text-[clamp(1.4rem,3.2vw,2.6rem)] leading-[1.02] tracking-[-0.02em] font-medium">
              {heading}
            </h2>
          )}
          <div className={`font-sans text-[17px] md:text-[18px] leading-[1.65] text-graphite space-y-4 ${heading ? "mt-7" : ""}`}>
            {body.split(/\n\n+/).map((para, p) => (
              // Using <div role="paragraph"> instead of <p> to prevent Safari
              // Reader Mode from auto-triggering on case-study subpages. Safari's
              // Reader heuristic specifically looks for sequences of <p> elements
              // with sufficient body-text length and "article-like" semantics —
              // exactly what these case studies happen to satisfy. Swapping the
              // tag to <div> keeps the visual layout identical (the parent's
              // `space-y-4` handles inter-paragraph spacing) while breaking the
              // Reader heuristic. The role="paragraph" preserves screen-reader
              // semantics for accessibility.
              <div role="paragraph" key={`p-${p}-${para.slice(0, 20)}`}>{para}</div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// Per-project frame overrides: render a custom frame instead of an editorial spread.
// Slot keys are 1-based frame numbers (frame 1 is the hero, frames 2–5 are editorial by default).
const LUCA_INTRO_IMG = {
  f2: "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/5oul052x_lucagrelli-5e1d70cb265cd17%402x.jpg",
  f3: "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/08j0gbhg_lucagrelli-5da7b9342623279%402x.jpg",
  f4: "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/5anqbv4l_lucagrelli-5da7c4bf26236f8%402x.jpg",
  f5: "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/cvj6n0py_lucagrelli-5e1d7056265cd08%402x.jpg"
};

export const FRAME_OVERRIDES = {
  // Project 01 (Luca Grelli) — alternating image/copy layout, with uploaded portraits.
  "project-01": {
    2: {
      type: "editorial",
      hideImage: false,
      side: "right", // image right, copy left
      imageSrc: LUCA_INTRO_IMG.f2,
      heading: "Hello. Ciao. こんにちわ. Hola. Hoi. Salut.",
      body:
        "Digitally savvy creative director with an international career that shaped me not only as a creative but as a person. I started in Milan and continued in Amsterdam (mostly at Wieden+Kennedy), New York (Razorfish, JWT, Johannes Leonardo,...), Tokyo, Barcelona and - as a freelancer - all over the world (Taxi in Toronto, Sid Lee in Montreal; VCCP, DDB in Berlin, BBH in Shanghai, Leo Burnett in Beirut, JWT in Paris to name a few)."
    },
    3: {
      type: "editorial",
      hideImage: false,
      side: "left", // image left, copy right
      imageSrc: LUCA_INTRO_IMG.f3,
      heading: "",
      body:
        "My multicultural upbringing and years of working with diverse people around the world gave me a deep understanding of local cultures and human behavior—an understanding that continues to shape my work.\n\nLong before the “Skip Ad” era, I focused on helping brands earn cultural relevance rather than simply demand attention. Across luxury, fashion, sports, automotive, design, telecom, entertainment, and youth culture, I have helped create work that adds value to people’s lives through meaningful collaborations, cultural participation, and experiences people actively choose to engage with."
    },
    4: {
      type: "editorial",
      hideImage: false,
      side: "right", // image right, copy left
      imageSrc: LUCA_INTRO_IMG.f4,
      heading: "",
      body:
        "As a result, much of my work has extended beyond conventional advertising formats. From sports tournaments and music videos to graphic novels, animated films, TV shows, video games, exhibitions, and social experiments such as the Mercedes-Benz Super Bowl Tweet Race, I have consistently sought to create ideas that people could participate in rather than simply consume—often blurring the boundaries between digital and physical experiences."
    },
    5: {
      type: "editorial",
      hideImage: false,
      side: "left", // image left, copy right
      imageSrc: LUCA_INTRO_IMG.f5,
      heading: "",
      body:
        "My love for people and cultures is closely matched by my passion for technology - I enthusiastically embrace it and experiment with it, but only as a tool to create emotional connections and experiences, not as a trendy gimmick.\n\nIf there’s one thing that my journeys around the world taught me, it’s that we might live in the age of tech and big data, but it’s empathy, craft and meaningful storytelling that will always matter most."
    }
  },
  // Project 26 (Welcome to Masterdam — Nike Netherlands) — frame 2 copy + Proov cover, frame 3 Vimeo film.
  "project-26": {
    2: {
      type: "editorial",
      heading: "",
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/3gx3h71s_R-1677088-1477577997-4244-topaz-face-upscale-4x.jpg",
      body:
        "To launch Nike’s first street football tournament in Amsterdam, we collaborated with local hip-hop group The Proov to create “Welcome to Masterdam – Feel the Heat,” a celebration of Dutch street football and Amsterdam’s proud football culture. We produced the music video for the track, which aired on TMF and MTV. Together, the song and video celebrated the hundreds of big-headed Amsterdam kids who believe that the country’s best footballer, Edgar Davids, plays just like them."
    },
    3: { type: "vimeo", videoId: "52696500" },
    4: { type: "skip" },
    5: { type: "skip" }
  },
  // Project 27 (Marlboro Country) — frame 2 full-frame Vimeo, frames 3–5 print ads at 90%.
  "project-27": {
    2: { type: "vimeo", videoId: "52832810" },
    3: {
      type: "image",
      scale: 0.9,
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/ag9denuh_lucagrelli-5bb4bc2924cb885%402x-topaz-upscale-4x.jpg"
    },
    4: {
      type: "image",
      scale: 0.9,
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/jupmot5m_lucagrelli-5bb4bc2924cb886%402x-topaz-upscale-4x.jpg"
    },
    5: {
      type: "image",
      scale: 0.9,
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/detbjjg1_lucagrelli-5bb4bc2924cb887%402x-topaz-upscale-4x.jpg"
    }
  },
  // Project 24 (Adidas Pride) — frame 2 copy, frame 3 Vimeo film.
  "project-25": {
    2: {
      type: "editorial",
      hideImage: true,
      heading: "",
      body:
        "Created for Adidas Pride, this global content series brought together LGBTQ+ athletes to share their experiences, challenge discrimination in sport, and inspire others to be themselves. Distributed through podcasts and video content across YouTube, Facebook, and Instagram, the series featured athletes including Tom Daley, Layshia Clarendon, and Chelsea Gray, and drew its visual identity from the Adidas Pride collection designed by artist Kris Andrew Small."
    },
    3: { type: "vimeo", videoId: "1195982293" },
    4: { type: "skip" },
    5: { type: "skip" }
  },
  // Project 23 (Godiva Japan — First Kiss) — frame 2 copy, frame 3 Vimeo film.
  "project-24": {
    2: {
      type: "editorial",
      hideImage: true,
      heading: "",
      body:
        "We asked 51 people about their first kiss. Then we made it happen again. For Godiva Japan, on Valentine's Day."
    },
    3: { type: "vimeo", videoId: "157812417" },
    4: { type: "skip" },
    5: { type: "skip" }
  },
  // Project 22 (Toot Underwear) — frame 2 copy, frames 3–4 Vimeo films.
  "project-23": {
    2: {
      type: "editorial",
      hideImage: true,
      heading: "",
      body:
        "Toot Underwear needed a memorable way to demonstrate its unique product benefit: keeping “it” in the ideal position. We created a playful online video that turned a functional feature into highly shareable entertainment, generating more than one million views on YouTube. Building on its success, we followed up with a special Christmas edition that gave the concept a seasonal twist."
    },
    3: { type: "vimeo", videoId: "157812418" },
    4: { type: "vimeo", videoId: "251427970" },
    5: { type: "skip" }
  },
  // Project 21 (Nike DFM — Designed for Movement) — frame 2 copy, frames 3–6 Jarnot artworks.
  "project-22": {
    2: {
      type: "editorial",
      hideImage: true,
      heading: "",
      body:
        "To launch Nike’s global DFM (Designed for Movement) apparel line, we collaborated with French illustrator Sébastien Jarnot to give visual form to the abstract idea of movement. The resulting series of more than 20 artworks was deployed worldwide for over two years, appearing in campaign materials and Niketown stores across the globe."
    },
    3: {
      type: "image",
      scale: 0.9,
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/x3ev48j2_lucagrelli-5bb4aa3824cb721%402x.jpg"
    },
    4: {
      type: "image",
      scale: 0.9,
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/rtnl4wwa_lucagrelli-5bb4aa3824cb722%402x.jpg"
    },
    5: {
      type: "image",
      scale: 0.9,
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/carg05q1_Screenshot%202026-06-23%20at%2016.00.36.JPG"
    },
    6: {
      type: "image",
      scale: 0.9,
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/nkisnyi3_lucagrelli-5bb4aa3824cb723%402x.jpg"
    }
  },
  // Project 20 (Mercedes-Benz Tweet Race) — frame 2 copy + title, frame 3 Vimeo, frames 4–5 screenshots.
  "project-21": {
    2: {
      type: "editorial",
      hideImage: true,
      heading: "The world’s first Twitter-fueled race",
      body:
        "To showcase Mercedes-Benz innovation and connect with a yonger audience, we created the world’s first Twitter-fueled race: a three-day, 1,400-mile journey across the US between four teams. Fans powered their favorite team by tweeting its hashtag, turning social media engagement into virtual fuel and making followers an integral part of the competition."
    },
    3: { type: "vimeo", videoId: "52696501" },
    4: {
      type: "image",
      scale: 0.9,
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/y9ytmpcp_lucagrelli-5bbcb18624cfef7%402x.jpg"
    },
    5: {
      type: "image",
      scale: 0.9,
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/6il9xg3q_lucagrelli-5bbcb18624cfef6%402x.jpg"
    }
  },
  // Project 19 (Smirnoff Be There) — frame 2 copy, frame 3 Vimeo film, frames 4–5 screenshots.
  "project-20": {
    2: {
      type: "editorial",
      hideImage: true,
      heading: "",
      body:
        "To position Smirnoff as the vodka of unmissable original parties, we built a digital hub for party-goers in search of extraordinary experiences. The platform enabled users to create and share unique party themes and ideas, rewarded them for organizing their own events, and helped them discover, document, and relive the most memorable parties happening across the US."
    },
    3: { type: "vimeo", videoId: "52695272" },
    4: {
      type: "image",
      scale: 0.9,
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/i8mr0fxq_lucagrelli-5bb4a97124cb708%402x.jpg"
    },
    5: {
      type: "image",
      scale: 0.9,
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/bsvn3lz0_lucagrelli-5bb4a97224cb709%402x.jpg"
    }
  },
  // Project 18 (Portfolio Night Tokyo) — frame 2 copy-only, frame 3 Vimeo film.
  "project-19": {
    2: {
      type: "editorial",
      hideImage: true,
      heading: "",
      body:
        "A quirky short film to promote Portfolio Night Tokyo, a one-day event in which advertising students get to meet the best creative directors and get their work reviewed."
    },
    3: { type: "vimeo", videoId: "157812419" },
    4: { type: "skip" },
    5: { type: "skip" }
  },
  // Project 17 (HAAS textile) — frame 2 copy-only, frames 3–5 print ads at 90%.
  "project-18": {
    2: {
      type: "editorial",
      hideImage: true,
      heading: "",
      body:
        "An Italian luxury textile brand with nearly 200 years of heritage wanted to reconnect with a younger, affluent audience without compromising its premium positioning. At a time when every brand was chasing digital and social attention, we took the opposite approach: a print-only campaign appearing exclusively in a curated selection of luxury, fashion, and design publications."
    },
    3: {
      type: "image",
      scale: 0.9,
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/xa6q9iov_lucagrelli-5c905bd525466e0%402x.jpg"
    },
    4: {
      type: "image",
      scale: 0.9,
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/h3fmykgw_lucagrelli-5c905bd525466de%402x.jpg"
    },
    5: {
      type: "image",
      scale: 0.9,
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/rigrnlvb_lucagrelli-5c905bd525466df%402x.jpg"
    }
  },
  // Project 16 (Bacardi 150th) — frame 2 copy-only, frames 3–5 Vimeo films.
  "project-17": {
    2: {
      type: "editorial",
      hideImage: true,
      heading: "",
      body:
        "To mark Bacardi’s 150th anniversary, we collaborated with choreographer Noemi Lafrance to bring defining moments from the brand’s history to life on stage. Crafted as living paintings, each performance recreated a milestone from Bacardi’s past and was captured as a series of short films for TV and social media."
    },
    3: { type: "vimeo", videoId: "52696208" },
    4: { type: "vimeo", videoId: "52695946" },
    5: { type: "vimeo", videoId: "52698160" }
  },
  // Project 15 (IKEA Japan — 10 years / 10 catalogues) — frame 2 full-frame Vimeo film, no further frames.
  "project-15": {
    2: { type: "vimeo", videoId: "163707053" },
    3: { type: "skip" },
    4: { type: "skip" },
    5: { type: "skip" }
  },
  // Project 16 (IKEA — Change feels good) — frame 2 full-frame Vimeo film, no further frames.
  "project-16": {
    2: { type: "vimeo", videoId: "92548199" },
    3: { type: "skip" },
    4: { type: "skip" },
    5: { type: "skip" }
  },
  // Project 14 (Nike SPQR graphic novel) — frame 2 copy + book cover, frames 3–6 spreads.
  "project-14": {
    2: {
      type: "editorial",
      heading: "",
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/orgtrg9t_lucagrelli-5bbc9d6d24cfd5e%402x.jpg",
      body:
        "To promote a new Nike 5-a-side football tournament, we collaborated with artist Danijel Zezelj to create a graphic novel and animated short. The story, set in 3001 Rome, follows Tiberio, a player from the impoverished South, challenging the North’s dominance in a city-wide football match."
    },
    3: { type: "vimeo", videoId: "294142723" },
    4: {
      type: "image",
      scale: 0.9,
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/byk0bmuz_lucagrelli-5bbc9d6e24cfd5f%402x.jpg"
    },
    5: {
      type: "image",
      scale: 0.9,
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/f65pqzsx_lucagrelli-5bbc9d6e24cfd60%402x.jpg"
    },
    6: {
      type: "image",
      scale: 0.9,
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/bqvhq9en_lucagrelli-5bbc9d6f24cfd62%402x.jpg"
    },
    7: {
      type: "image",
      scale: 0.9,
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/vp9qbngc_lucagrelli-5bbc9d6d24cfd5d%402x.jpg"
    }
  },
  // Project 13 (Toyota Dream Car) — frame 2 copy-only, frames 3–7 Vimeo films.
  "project-13": {
    2: {
      type: "editorial",
      hideImage: true,
      heading: "",
      body:
        "For Toyota, we asked thousands of children around the world to draw and describe the car of their dreams. Through a social campaign called “Dream Car of the Day” we then brought to life the inspiring artwork of 90 children using 2D & 3D animation."
    },
    3: { type: "vimeo", videoId: "157886901" },
    4: { type: "vimeo", videoId: "157886909" },
    5: { type: "vimeo", videoId: "157886902" },
    6: { type: "vimeo", videoId: "157886904" },
    7: { type: "vimeo", videoId: "157886903" }
  },
  // Project 12 (Armani Crossroads season 5) — frame 2 copy-only, frame 3 Vimeo film.
  "project-12": {
    2: {
      type: "editorial",
      hideImage: true,
      heading: "",
      body:
        "Five women, five vivid meditations on choice and self-discovery. Tecla Insolia, Yuanyuan Tan, Chloe Fineman, Alissa Jung and Sheila Atim come together in that physical and emotional space at the intersection of what is and what might be. Armani Crossroads season 5, directed by Celine Song."
    },
    3: { type: "vimeo", videoId: "1196133326" },
    4: { type: "skip" },
    5: { type: "skip" }
  },
  // Project 11 — frame 2 full-frame Vimeo film, no further frames.
  "project-11": {
    2: { type: "vimeo", videoId: "52695422" },
    3: { type: "skip" },
    4: { type: "skip" },
    5: { type: "skip" }
  },
  // Project 10 (Adidas FIFA World Cup) — frame 2 copy-only, frames 3–6 Vimeo films.
  "project-10": {
    2: {
      type: "editorial",
      hideImage: true,
      heading: "",
      body:
        "Before Adidas became Nike's me-too, its communication was much more lighthearted, quirky and humorous. Featuring the brand's greatest sponsored players - from Beckham to Zidane and Del Piero - and even world star referee Collina, the campaign helped Adidas overtake Nike in Asia with a +30% in sales, and even gain ground in the US, where sales went up 9%. Today, the campaign is still considered one of the most memorable World Cup campaigns of all times by many global football and advertising publications (campaign.com, marketingweek.com, goal.com, shortlist.com, indianexpress.com, sportingnews.com, adforum.com and more)."
    },
    3: { type: "vimeo", videoId: "52693728" },
    4: { type: "vimeo", videoId: "52693789" },
    5: { type: "vimeo", videoId: "52693869" },
    6: { type: "vimeo", videoId: "24669142" }
  },
  // Project 09 (Arctic Basecamp) — frame 2 copy override, frame 3 full-frame photo.
  "project-09": {
    2: {
      type: "editorial",
      hideImage: true,
      heading: "",
      body:
        "Arctic Basecamp is a collective of climate scientists dedicated to bringing the reality of climate change directly to decision-makers. To highlight the consequences of climate inaction, we transported a real iceberg from the Arctic to the COP26 climate conference in Glasgow and let it melt outside the venue."
    },
    3: {
      type: "image",
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/8g9chjza_lucagrelli-624ef76c2933f89%402x.jpg"
    },
    4: {
      type: "editorial",
      hideImage: true,
      heading: "",
      body:
        "Alongside the iceberg, we distributed 1,000 bottles of “Arctic Melt”, filled with water from genuine melted icebergs, transforming an abstract crisis into a tangible experience for world leaders, delegates and journalists."
    },
    5: {
      type: "image",
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/smwxx59t_Screenshot%202026-06-23%20at%2012.41.25.JPG"
    }
  },
  // Project 08 — frame 2 full-frame Vimeo film, no further frames.
  "project-08": {
    2: { type: "vimeo", videoId: "1077686246" },
    3: { type: "skip" },
    4: { type: "skip" },
    5: { type: "skip" }
  },
  // Project 07 — frame 2 full-frame Vimeo film, no further frames.
  "project-07": {
    2: { type: "vimeo", videoId: "1077672094" },
    3: { type: "skip" },
    4: { type: "skip" },
    5: { type: "skip" }
  },
  // Project 06 — frame 2 full-frame Vimeo film, no further frames.
  "project-06": {
    2: { type: "vimeo", videoId: "1077665079" },
    3: { type: "skip" },
    4: { type: "skip" },
    5: { type: "skip" }
  },
  // Project 05 (Belgium Red Devils) — frame 2 copy, frames 3–5 titled posters.
  "project-05": {
    2: {
      type: "editorial",
      hideImage: true,
      heading: "Prayers won't help",
      body:
        "Red Devils is the nickname of the Belgian national football team.\n\nThe three executions featured three of the team’s best players and ran on oversized billboards since they were meant to be looked at from a distance, for the optical illusion to work. Diabolic, isn’t it?"
    },
    3: {
      type: "titledImage",
      heading: "Prayers won't help",
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/xpskeds3_lucagrelli-5bb45c5d24ca970%402x.jpg",
      background: "bg-paper",
      textColor: "text-ink"
    },
    4: {
      type: "titledImage",
      heading: "Don't play with fire",
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/odi30t1t_lucagrelli-5bb45c5d24ca972%402x.jpg",
      background: "bg-paper",
      textColor: "text-ink"
    },
    5: {
      type: "titledImage",
      heading: "You will not rest in peace",
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/4kvzyfzm_lucagrelli-5bb45c5e24ca974%402x.jpg",
      background: "bg-paper",
      textColor: "text-ink"
    }
  },
  // Project 04 (Nike 5-a-side / Calcetto Champions Cup) — frame 2 & 3 copy + image overrides.
  "project-04": {
    2: {
      type: "editorial",
      heading: "Nike 5-a-side football: enter the arena",
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/tzs5035b_lucagrelli-5bb4b72324cb7bc%402x.jpg",
      body:
        "Inspired by the gladiators who entered the Colosseum knowing they faced the ultimate challenge, we used the salute “Morituri te salutant” (“those who are about to die salute you”) to launch the Nike Calcetto Champions Cup as Italy’s toughest 5-a-side tournament."
    },
    3: {
      type: "editorial",
      heading: "",
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/43p1ftx3_lucagrelli-5bb4b72324cb7bf%402x.jpg",
      body:
        "To celebrate the players who survived the competition’s toughest battles, we partnered with Panini, the iconic Italian publisher behind the world’s most famous football sticker albums, to create an official collection featuring every player from the 16 teams that reached the finals. Each sticker was also reproduced as a full-size poster, turning local footballers into heroes."
    },
    4: {
      type: "image",
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/xau17cgp_lucagrelli-5bb4b72324cb7bd%402x.jpg",
      background: "bg-black"
    },
    5: {
      type: "image",
      imageSrc:
        "https://customer-assets.emergentagent.com/job_gallery-scroll/artifacts/3epoh0xz_lucagrelli-5bb4b72324cb7be%402x.jpg",
      background: "bg-black"
    }
  },
  // Project 03 (Adidas Fevernova) — frame 2 copy-only (no image), frame 3 Vimeo film.
  "project-03": {
    2: {
      type: "editorial",
      hideImage: true,
      heading: "Own the game",
      body:
        "Introducing the new Adidas Fevernova official World Cup ball. In 2014 Buzzfeed ranked the ad among the top 10 football commercials of all times."
    },
    3: { type: "vimeo", videoId: "157918487" },
    4: { type: "skip" },
    5: { type: "skip" }
  },
  // Project 02 (EA Burnout) — frame 2 is a custom copy-only editorial (no image
  // placeholder), frame 3 plays the Vimeo film, frames 4 and 5 are skipped.
  "project-02": {
    2: {
      type: "editorial",
      hideImage: true,
      heading: "Sink that ship - The music video",
      body:
        "For the European launch of the Burnout Revenge video game we collaborated with the band Kaiser Chiefs and released a 3'00\" music video for their song Sink That Ship featured in the game's soundtrack. A story about competition, crashing cars and, well, good old pointless revenge (45\" excerpt shown here)."
    },
    3: { type: "vimeo", videoId: "294149117" },
    4: { type: "skip" },
    5: { type: "skip" }
  }
};

// Vimeo frame — stock Vimeo embed. Native Vimeo player handles thumbnail,
// play button, sound, fullscreen, mute, and rotation. No SDK manipulation, no
// custom controls, no facade. The iframe is letterboxed within the viewport
// (no cropping, no overflow). Vimeo's native player respects the user's device
// settings (silent switch, system volume) and works identically across all
// platforms.
function VimeoFrame({ frameNumber, videoId }) {
  const src =
    `https://player.vimeo.com/video/${videoId}` +
    `?dnt=1&title=0&byline=0&portrait=0&autopause=0&outro=nothing`;

  return (
    <section
      id={`case-frame-${frameNumber}`}
      data-testid={`case-frame-${frameNumber}`}
      className="portfolio-snap relative w-full h-screen overflow-hidden bg-paper"
    >
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black"
        style={{ width: "100%", height: "56.25vw" }}
      >
        <iframe
          src={src}
          title={`Vimeo ${videoId}`}
          data-testid={`case-frame-${frameNumber}-iframe`}
          className="absolute inset-0 w-full h-full"
          style={{ border: 0 }}
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
          loading="lazy"
          allowFullScreen
        />
      </div>
    </section>
  );
}

// Full-frame image — fits entirely within the viewport (object-contain, no cropping).
// When `scale` < 1 the image is also inset on all sides for editorial breathing room.
function FullImageFrame({ frameNumber, imageSrc, background = "bg-paper", scale = 1 }) {
  const inset = scale < 1 ? `${((1 - scale) / 2) * 100}%` : "0";
  return (
    <section
      id={`case-frame-${frameNumber}`}
      data-testid={`case-frame-${frameNumber}`}
      className={`portfolio-snap relative w-full h-screen overflow-hidden ${background}`}
    >
      <div
        className="absolute"
        style={{ top: inset, bottom: inset, left: inset, right: inset }}
      >
        <img
          src={imageSrc}
          alt=""
          loading="lazy"
          className="w-full h-full object-contain"
        />
      </div>
    </section>
  );
}

// Titled image — title above, image contained with breathing room (object-contain).
function TitledImageFrame({
  frameNumber,
  imageSrc,
  heading,
  background = "bg-black",
  textColor = "text-white"
}) {
  return (
    <section
      id={`case-frame-${frameNumber}`}
      data-testid={`case-frame-${frameNumber}`}
      className={`portfolio-snap relative w-full h-screen overflow-hidden ${background} ${textColor}`}
    >
      <div className="relative z-10 w-full h-full max-w-[1600px] mx-auto px-6 md:px-10 py-6 md:py-9 flex flex-col items-center justify-center gap-2 md:gap-5">
        {heading && (
          <h2
            data-testid={`case-frame-${frameNumber}-heading`}
            className="font-display text-[clamp(1.08rem,1.76vw,1.5rem)] leading-[1.1] tracking-[-0.01em] font-medium text-center"
          >
            {heading}
          </h2>
        )}
        <img
          src={imageSrc}
          alt=""
          loading="lazy"
          data-testid={`case-frame-${frameNumber}-image`}
          className="max-w-full max-h-[78vh] md:max-h-[82vh] object-contain"
        />
      </div>
    </section>
  );
}


export const EDITORIAL_BLOCKS = [
  {
    heading: "The brief, in one line.",    body:
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse ut nibh in metus volutpat egestas. A short opening paragraph that frames the problem the work was asked to solve — written like a magazine standfirst, not an agency deck.",
    side: "right"
  },
  {
    heading: "How we approached it.",
    body:
      "Maecenas vehicula, magna at ultrices vulputate, justo lectus volutpat libero. We tend to start by listening, then arguing, then drawing. Placeholder bodycopy that you can replace with the studio's working method — process notes, references, the constraints that shaped the final form.",
    side: "left"
  },
  {
    heading: "The work, in detail.",
    body:
      "Curabitur scelerisque elit eget arcu vehicula, ut feugiat odio convallis. The deliverables, the craft choices, the small decisions that no one ever notices. Replace this paragraph with the things you actually want the reader to feel — material, scale, sound, tempo.",
    side: "right"
  },
  {
    heading: "What it did.",
    body:
      "Donec ut lectus eget eros tincidunt commodo. Outcomes, awards, press, sales — whichever metric is honest. Or skip the numbers and write about what the work taught you. Placeholder bodycopy for the closing beat of the case study.",
    side: "left"
  }
];

export default function CaseStudy() {
  const { slug } = useParams();
  const { notifyDestinationReady } = useTransition();

  // Try to resolve `slug` against the static projects array first (fast,
  // synchronous). If that fails, we fall back to fetching custom projects
  // on mount — a custom project's sub-page uses the same routing scheme
  // but its data lives in Mongo, not `data/projects.js`.
  const staticProjectIndex = findProjectBySlug(slug);
  const staticProject =
    staticProjectIndex >= 0 ? projects[staticProjectIndex] : null;

  // Custom-project resolution. Fetched on mount; `null` while pending so
  // we don't 404 prematurely while the network call is in flight.
  const [customProject, setCustomProject] = useState(null);
  const [customLoaded, setCustomLoaded] = useState(false);
  useEffect(() => {
    // Skip the fetch entirely when the static match already resolved —
    // custom projects live in a separate collection so a static hit means
    // we're already done.
    if (staticProject) {
      setCustomLoaded(true);
      return;
    }
    if (!slug) {
      setCustomLoaded(true);
      return;
    }
    let cancelled = false;
    fetchCustomProjects()
      .then((list) => {
        if (cancelled) return;
        const match = (list || []).find((c) => c.slug === slug);
        setCustomProject(match || null);
        setCustomLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setCustomLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, staticProject]);

  // Unified project shape — either the static project object (with baked-in
  // fields) or a synthesised view of the custom project doc that carries
  // just enough for the case-study renderer.
  const project = useMemo(() => {
    if (staticProject) return staticProject;
    if (customProject) {
      const heroImage =
        customProject.media_poster || customProject.media_src || "";
      return {
        title: customProject.title || "Untitled",
        slug: customProject.slug,
        transitionImage: heroImage,
        media: {
          type: customProject.media_type || "image",
          src: customProject.media_src || "",
          ...(customProject.media_poster
            ? { poster: customProject.media_poster }
            : {}),
        },
        _isCustom: true,
      };
    }
    return null;
  }, [staticProject, customProject]);

  // Frames: static projects get the source-code multi-frame builder that
  // populates neighbour images into slots 2–5. Custom projects only have
  // a hero frame by default; extra frames come exclusively from the
  // admin case-study overrides (fetched below).
  const frames = useMemo(() => {
    if (staticProject && staticProjectIndex >= 0) {
      return buildCaseStudyFrames(staticProjectIndex);
    }
    if (customProject) {
      return [customProject.media_poster || customProject.media_src || ""];
    }
    return [];
  }, [staticProject, staticProjectIndex, customProject]);

  // Admin-edited frame overrides fetched from the CMS. Merged with the
  // module-scope FRAME_OVERRIDES per-frame in `resolveFrame`: admin doc
  // wins if present, otherwise the source-code default is used. Fetch is
  // fire-and-forget on mount; while it pends the source-code defaults
  // render immediately (no loading spinner needed since the case study is
  // already interactive with baked-in content).
  const [adminFrames, setAdminFrames] = useState(null);
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    fetchCaseStudyOverrides()
      .then((all) => {
        if (cancelled) return;
        setAdminFrames(all?.[slug] || {});
      })
      .catch((err) => {
        // Non-fatal — fall back to baked-in defaults.
        console.warn("[CaseStudy] failed to load admin frame overrides:", err);
        setAdminFrames({});
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Hard-reset scroll to top before the first paint — the case-study landing must
  // line up exactly with the end-state of the click transition overlay. iOS
  // Safari and some Android browsers occasionally preserve the originating
  // scroll position across SPA route changes (the Portfolio sections and the
  // CaseStudy frames both use a 100lvh snap grid, so a preserved scrollY of
  // N × 100lvh lands the user on frame N instead of frame 1). We defeat this
  // with: overflow:hidden lock + multiple scrollTo(0,0) passes (sync + rAF
  // chain + 100ms fallback) before re-enabling snap.
  useLayoutEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevSnap = html.style.scrollSnapType;
    const prevBehavior = html.style.scrollBehavior;

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    html.style.scrollSnapType = "none";
    html.style.scrollBehavior = "auto";

    // Detect whether TransitionContext applied the overflow:hidden lock for
    // the mobile/iOS frame-2 defense. On desktop with classic scrollbars,
    // TransitionContext skips the lock to avoid the scrollbar reflow that
    // produces a visible leftward content shift. We mirror that decision
    // here so we don't introduce the same shift ourselves.
    const lockActive = html.style.overflow === "hidden";
    if (lockActive) {
      // Re-apply on body in case body wasn't locked (defensive).
      html.style.overflow = "hidden";
      body.style.overflow = "hidden";
    }
    window.scrollTo(0, 0);

    const resetAndRelease = () => {
      window.scrollTo(0, 0);
      html.style.scrollSnapType = prevSnap;
      html.style.scrollBehavior = prevBehavior;
      // NOTE: when lockActive (mobile path), we deliberately do NOT release
      // overflow:hidden here. The lock is held until notifyDestinationReady
      // fires inside TransitionContext, which releases overflow one rAF
      // BEFORE removing the overlay. This gives iOS Safari's snap engine a
      // frame to recompute *under* the still-visible overlay so the user
      // never sees a tiny up/down "shake" at handoff. The cleanup return
      // below catches the lock release if the user navigates away before
      // notifyDestinationReady fires (e.g., rapid back-button).
      // paddingRight is no longer used (iter-15) since we no longer set
      // overflow:hidden on desktop; safe to clear unconditionally as a
      // defensive measure for any old in-flight state.
      html.style.paddingRight = "";
    };

    // Pass 1: next animation frame.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      // Pass 2: the frame after that — by now React has committed to the DOM
      // and any browser-restored scroll has been re-applied (and re-reset).
      raf2 = requestAnimationFrame(resetAndRelease);
    });
    // Safety net for iOS Safari where rAF can fire before the route-change
    // scroll preservation is applied.
    const tFallback = setTimeout(() => {
      window.scrollTo(0, 0);
      resetAndRelease();
    }, 100);

    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      clearTimeout(tFallback);
      // Make sure we don't leave the page locked if the component unmounts
      // before the fallback fires.
      if (lockActive) {
        html.style.overflow = "";
        body.style.overflow = "";
      }
      html.style.paddingRight = "";
    };
    // Mount-only on purpose: this is the route-transition scroll lock — it
    // must run exactly once when CaseStudy mounts, not on prop/state churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tell the global transition overlay it can release once frame 1's image
  // has actually been decoded / committed to the screen.
  useEffect(() => {
    if (!project || !notifyDestinationReady) return;
    const img = document.querySelector('[data-testid="case-frame-1-image"] img');
    if (!img) {
      notifyDestinationReady();
      return;
    }
    const fire = () => notifyDestinationReady();
    // img.decode() resolves only AFTER the image has been fully decoded and
    // is ready to paint without further reflow — much stronger guarantee
    // than `complete && naturalWidth > 0`, which can be true while the
    // browser is still decoding. This eliminates the late-paint reflow that
    // looked like a tiny jump at the end of the mobile transition.
    if (typeof img.decode === "function") {
      img.decode().then(fire).catch(fire);
    } else if (img.complete && img.naturalWidth > 0) {
      fire();
    } else {
      img.addEventListener("load", fire, { once: true });
      img.addEventListener("error", fire, { once: true });
    }
    // Safety net in case neither event fires.
    const t = setTimeout(fire, 800);
    return () => {
      img.removeEventListener("load", fire);
      img.removeEventListener("error", fire);
      clearTimeout(t);
    };
    // `img`, `fire`, `t` are locals scoped to the effect body — not deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, notifyDestinationReady]);

  // Memoize the sorted list of "extra" frame numbers (beyond default 5) declared in
  // FRAME_OVERRIDES so we don't rebuild this array on every render.
  const extraFrameNumbers = useMemo(() => {
    const source = { ...(FRAME_OVERRIDES[slug] || {}), ...(adminFrames || {}) };
    return Object.keys(source)
      .map((k) => Number(k))
      .filter((n) => n > 5)
      .sort((a, b) => a - b);
    // FRAME_OVERRIDES is a module-scope constant; `a`, `b` are sort callback
    // args; `Number`, `Object.keys` are globals — none belong in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, adminFrames]);

  if (!project) {
    // Wait for the custom-project fetch to complete before redirecting —
    // otherwise a valid custom slug flashes home for one render before
    // the network resolves it.
    if (!customLoaded) return null;
    return <Navigate to="/" replace />;
  }

  const totalFrames = frames.length || 5;
  // Origin section on the home page that this case-study returns to.
  // NOTE: We do NOT pass a positional sectionId here — admin visibility/order
  // + custom projects can move a hero to a different visible-list position
  // between click and return. Portfolio.jsx resolves `slug` to the correct
  // section id after its async admin fetches populate the visible list.
  return (
    <div
      data-testid="case-study-root"
      role="application"
      aria-label="Project case study"
      className="relative w-full bg-paper text-ink"
    >
      {/* Minimal case-study header — single "< BACK" link in the top-left.
          Routing to "/" with state.scrollToSectionId triggers Portfolio's
          existing scroll-restoration + return-overlay crossfade, giving a
          smooth transition back to the originating project's main frame. */}
      <header
        data-testid="case-study-header"
        className="fixed top-0 left-0 w-full z-50 px-6 md:px-10 py-6 md:py-7 mix-blend-difference text-white pointer-events-none"
      >
        <Link
          to="/"
          state={{
            slug,
            image: project.transitionImage
          }}
          data-testid="case-study-back"
          className="pointer-events-auto font-sans text-[10px] md:text-[11px] tracking-[0.28em] uppercase hover:opacity-60 transition-opacity duration-500"
        >
          ← Back
        </Link>
      </header>

      {/* Frame 1 — hero (image with white frame, matches end-state of click transition) */}
      <HeroFrame src={frames[0]} />

      {/* Frames 2–5 — editorial layouts by default for STATIC projects.
          Custom projects have no source-code defaults, so we pass `null`
          for the default block; the renderer then skips the frame unless
          the admin has explicitly overridden it. Per-project source-code
          FRAME_OVERRIDES can still replace or skip any slot on statics. */}
      {EDITORIAL_BLOCKS.map((block, i) => {
        const frameNumber = i + 2;
        const defaultBlock = project._isCustom ? null : block;
        return renderOverrideOrEditorial(slug, frameNumber, defaultBlock, totalFrames, adminFrames);
      })}

      {/* Extra frames beyond the default 5 — only rendered when explicitly defined in FRAME_OVERRIDES. */}
      {extraFrameNumbers.map((frameNumber) =>
        renderOverrideOrEditorial(slug, frameNumber, project._isCustom ? null : EDITORIAL_BLOCKS[(frameNumber - 2) % EDITORIAL_BLOCKS.length], totalFrames, adminFrames)
      )}
    </div>
  );
}

// Shared frame renderer — picks an override (vimeo/image/titledImage/editorial/skip)
// or falls back to the default editorial block for that slot.
// `adminFrames` is the map fetched from /api/projects/case_study_overrides
// for the current slug — its docs (backend snake_case) shadow the module-
// scope FRAME_OVERRIDES per-frame when present.
function renderOverrideOrEditorial(slug, frameNumber, block, totalFrames, adminFrames) {
  const admin = adminFrames?.[frameNumber];
  const sourceOverride = FRAME_OVERRIDES[slug]?.[frameNumber];
  // Normalise admin doc (snake_case → camelCase used everywhere else in the
  // renderer). Admin doc wins outright when present so a full-frame swap
  // doesn't inherit stale fields from the source-code default.
  const override = admin
    ? {
        type: admin.type,
        heading: admin.heading,
        body: admin.body,
        imageSrc: admin.image_src,
        videoId: admin.video_id,
        side: admin.side,
        hideImage: admin.hide_image,
        background: admin.background,
        textColor: admin.text_color,
        scale: admin.scale,
      }
    : sourceOverride;

  if (override?.type === "skip") return null;
  if (override?.type === "vimeo") {
    return (
      <VimeoFrame key={frameNumber} frameNumber={frameNumber} videoId={override.videoId} />
    );
  }
  if (override?.type === "image") {
    return (
      <FullImageFrame
        key={frameNumber}
        frameNumber={frameNumber}
        imageSrc={override.imageSrc}
        background={override.background}
        scale={override.scale}
      />
    );
  }
  if (override?.type === "titledImage") {
    return (
      <TitledImageFrame
        key={frameNumber}
        frameNumber={frameNumber}
        imageSrc={override.imageSrc}
        heading={override.heading}
        background={override.background}
        textColor={override.textColor}
      />
    );
  }
  if (!block && override?.type !== "editorial") return null;
  const heading = override?.type === "editorial" ? override.heading : block?.heading;
  const body = override?.type === "editorial" ? override.body : block?.body;
  const hideImage = override?.type === "editorial" ? override.hideImage : false;
  const side = override?.type === "editorial" && override.side ? override.side : block?.side;
  const imageSrc = override?.type === "editorial" ? override.imageSrc : undefined;
  return (
    <EditorialFrame
      key={frameNumber}
      frameNumber={frameNumber}
      totalFrames={totalFrames}
      side={side}
      heading={heading}
      body={body}
      hideImage={hideImage}
      imageSrc={imageSrc}
    />
  );
}
